import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, qs, type Paged } from '../api';
import { useAdmin } from '../auth';
import { Btn, Empty, ErrorBox, Loading, Modal, Pager, REJECT_REASONS, ROLE_RU, STATUS_RU, fmtDate, useAction } from '../ui';
import { ACTION_RU } from './moderation';

// ---------------------------------------------------------------- права ролей

export const PERMISSION_RU: Record<string, string> = {
  'stats.view': 'Сводка и аналитика',
  'system.view': 'Состояние системы',
  'listings.view': 'Просмотр объявлений',
  'listings.moderate': 'Модерация объявлений',
  'reports.view': 'Просмотр жалоб',
  'reports.resolve': 'Решение по жалобам',
  'users.view': 'Просмотр пользователей',
  'users.block': 'Блокировка пользователей',
  'categories.manage': 'Категории',
  'locations.manage': 'География',
  'translations.manage': 'Переводы',
  'search.manage': 'Поиск: синонимы и статистика запросов',
  'payments.view': 'Платежи (Stars): просмотр',
  'payments.refund': 'Платежи (Stars): возврат',
  'banners.manage': 'Баннеры',
  'promotions.manage': 'Продвижение',
  'business.manage': 'Бизнес-профили',
  'settings.manage': 'Настройки',
  'tariffs.manage': 'Тарифы',
  'admins.manage': 'Администраторы и роли',
  'audit.view': 'Журнал аудита',
  'deletion_requests.process': 'Запросы на удаление данных',
};

interface RoleMatrix {
  permissions: string[];
  ownerOnly: string[];
  roles: { role: string; editable: boolean; defaults: string[]; effective: string[]; overrides: { permission: string; granted: boolean }[] }[];
}

export function RolesPage() {
  const qc = useQueryClient();
  const action = useAction();
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['roles'], queryFn: () => api<RoleMatrix>('/admin/roles') });
  const reload = () => void qc.invalidateQueries({ queryKey: ['roles'] });

  const toggle = (role: string, permission: string, granted: boolean) =>
    action.run(async () => {
      await api(`/admin/roles/${role}/permissions/${permission}`, { method: 'PUT', json: { granted } });
      reload();
    });
  const reset = (role: string) =>
    action.run(async () => {
      if (!window.confirm(`Вернуть права роли «${ROLE_RU[role] ?? role}» к значениям по умолчанию?`)) return;
      await api(`/admin/roles/${role}/overrides`, { method: 'DELETE' });
      reload();
    });

  return (
    <div>
      <h1>Роли и права</h1>
      <p className="muted">
        Владелец всегда имеет все права. Права «Администраторы и роли» есть только у владельца. Изменённые относительно значений по
        умолчанию ячейки выделены. Изменения применяются в течение ~15 секунд без повторного входа; каждое записывается в журнал.
      </p>
      {action.error ? <ErrorBox error={action.error} /> : null}
      {isLoading ? <Loading /> : error || !data ? <ErrorBox error={error} onRetry={() => void refetch()} /> : (
        <table className="matrix">
          <thead>
            <tr>
              <th>Право</th>
              {data.roles.map((r) => (
                <th key={r.role}>
                  {ROLE_RU[r.role] ?? r.role}
                  {r.editable && r.overrides.length ? (
                    <div>
                      <button className="link" disabled={action.busy} onClick={() => void reset(r.role)}>
                        сбросить ({r.overrides.length})
                      </button>
                    </div>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.permissions.map((p) => (
              <tr key={p}>
                <td>
                  {PERMISSION_RU[p] ?? p}
                  <div className="muted">{p}</div>
                </td>
                {data.roles.map((r) => {
                  const on = r.effective.includes(p);
                  const locked = !r.editable || data.ownerOnly.includes(p);
                  const changed = r.overrides.some((o) => o.permission === p);
                  return (
                    <td key={r.role} className={changed ? 'changed' : undefined}>
                      <input
                        type="checkbox"
                        aria-label={`${ROLE_RU[r.role] ?? r.role}: ${PERMISSION_RU[p] ?? p}`}
                        checked={on}
                        disabled={locked || action.busy}
                        onChange={(e) => void toggle(r.role, p, e.target.checked)}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- бизнес-профили

interface BusinessRow {
  id: string;
  name: string;
  description: string | null;
  status: 'pending' | 'active' | 'suspended';
  isVerified: boolean;
  verifiedNote: string | null;
  activeUntil: string | null;
  expired: boolean;
  user: { id: string; firstName: string; username: string | null; status: string };
  updatedAt: string;
  requestedByUser: boolean;
}

const BUSINESS_STATUS_RU: Record<string, string> = { pending: 'Ожидает', active: 'Активен', suspended: 'Приостановлен' };

export function BusinessPage() {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<BusinessRow | null>(null);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['business', status, page],
    queryFn: () => api<Paged<BusinessRow>>(`/admin/business-profiles${qs({ status, page, pageSize: 20 })}`),
  });
  return (
    <div>
      <h1>Бизнес-профили</h1>
      <p className="muted">
        Онлайн-оплаты нет: профиль назначается вручную после оплаты по договору. Отметка «Проверен» ставится только с указанием, что
        именно проверено (документ, дата) — пользователи увидят её в карточке продавца.
      </p>
      <div className="toolbar">
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">Все</option>
          <option value="active">Активные</option>
          <option value="pending">Ожидают</option>
          <option value="suspended">Приостановленные</option>
        </select>
        <Btn onClick={() => setCreating(true)}>Создать профиль</Btn>
      </div>
      {isLoading ? <Loading /> : error ? <ErrorBox error={error} onRetry={() => void refetch()} /> : !data?.items.length ? <Empty /> : (
        <>
          <table>
            <thead><tr><th>Название</th><th>Пользователь</th><th>Статус</th><th>Действует до</th><th>Проверка</th></tr></thead>
            <tbody>
              {data.items.map((b) => (
                <tr key={b.id} className="clickable" onClick={() => setOpen(b)}>
                  <td><strong>{b.name}</strong>{b.description ? <div className="muted">{b.description}</div> : null}</td>
                  <td>{b.user.firstName} {b.user.username ? `@${b.user.username}` : ''}<div className="muted">#{b.user.id}</div></td>
                  <td>{BUSINESS_STATUS_RU[b.status]}{b.expired ? <span className="pill pill-warn">срок истёк</span> : null}{b.status === 'pending' && b.requestedByUser ? <div className="muted">заявка из приложения</div> : null}</td>
                  <td>{b.activeUntil ? fmtDate(b.activeUntil) : 'бессрочно'}</td>
                  <td>{b.isVerified ? <span className="pill">проверен</span> : '—'}{b.verifiedNote ? <div className="muted">{b.verifiedNote}</div> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pageSize={20} total={data.total} onPage={setPage} />
        </>
      )}
      {creating ? <CreateBusiness onClose={() => setCreating(false)} /> : null}
      {open ? <EditBusiness key={open.id} profile={open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

function CreateBusiness({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const action = useAction();
  const [userId, setUserId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [months, setMonths] = useState(1);
  const [activate, setActivate] = useState(true);
  return (
    <Modal title="Новый бизнес-профиль" onClose={onClose}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          void action.run(async () => {
            await api('/admin/business-profiles', {
              method: 'POST',
              json: { userId: userId.trim(), name: name.trim(), description: description.trim() || undefined, months, activate },
            });
            void qc.invalidateQueries({ queryKey: ['business'] });
            onClose();
          });
        }}
      >
        <label className="field">ID пользователя (из раздела «Пользователи»)<input value={userId} inputMode="numeric" pattern="\d{1,19}" required onChange={(e) => setUserId(e.target.value)} /></label>
        <label className="field">Название<input value={name} minLength={2} maxLength={80} required onChange={(e) => setName(e.target.value)} /></label>
        <label className="field">Описание<textarea value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} /></label>
        <label className="field">Срок, месяцев (0 — бессрочно)<input type="number" min={0} max={36} value={months} onChange={(e) => setMonths(Number(e.target.value))} /></label>
        <label className="check"><input type="checkbox" checked={activate} onChange={(e) => setActivate(e.target.checked)} /> Активировать сразу (пользователь получит уведомление)</label>
        <Btn type="submit" busy={action.busy}>Создать</Btn>
        {action.error ? <ErrorBox error={action.error} /> : null}
      </form>
    </Modal>
  );
}

function EditBusiness({ profile, onClose }: { profile: BusinessRow; onClose: () => void }) {
  const qc = useQueryClient();
  const action = useAction();
  const [name, setName] = useState(profile.name);
  const [description, setDescription] = useState(profile.description ?? '');
  const [note, setNote] = useState(profile.verifiedNote ?? '');
  const patch = (json: Record<string, unknown>) =>
    action.run(async () => {
      await api(`/admin/business-profiles/${profile.id}`, { method: 'PATCH', json });
      void qc.invalidateQueries({ queryKey: ['business'] });
      onClose();
    });
  return (
    <Modal title={`Бизнес-профиль #${profile.id}`} onClose={onClose}>
      <div className="form">
        <p className="muted">
          {profile.user.firstName} (#{profile.user.id}) · {BUSINESS_STATUS_RU[profile.status]} · до {profile.activeUntil ? fmtDate(profile.activeUntil) : 'бессрочно'}
        </p>
        <label className="field">Название<input value={name} minLength={2} maxLength={80} onChange={(e) => setName(e.target.value)} /></label>
        <label className="field">Описание<textarea value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} /></label>
        <Btn kind="secondary" busy={action.busy} disabled={name.trim().length < 2} onClick={() => void patch({ name: name.trim(), description: description.trim() || null })}>Сохранить</Btn>

        <h3>Срок и статус</h3>
        <div className="decision">
          <Btn kind="secondary" busy={action.busy} onClick={() => void patch({ extendMonths: 1 })}>+1 месяц</Btn>
          <Btn kind="secondary" busy={action.busy} onClick={() => void patch({ extendMonths: 12 })}>+12 месяцев</Btn>
          {profile.status !== 'active' ? <Btn busy={action.busy} onClick={() => void patch({ status: 'active' })}>Активировать</Btn> : null}
          {profile.status === 'active' ? <Btn kind="danger" busy={action.busy} onClick={() => void patch({ status: 'suspended' })}>Приостановить</Btn> : null}
        </div>

        <h3>Проверка</h3>
        {profile.isVerified ? (
          <>
            <p>Проверен: {profile.verifiedNote}</p>
            <Btn kind="danger" busy={action.busy} onClick={() => void patch({ isVerified: false })}>Снять отметку</Btn>
          </>
        ) : (
          <div className="decision">
            <input placeholder="Что проверено: документ, номер, дата (обязательно)" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
            <Btn busy={action.busy} disabled={note.trim().length < 5} onClick={() => void patch({ isVerified: true, verifiedNote: note.trim() })}>Отметить проверенным</Btn>
          </div>
        )}
        {action.error ? <ErrorBox error={action.error} /> : null}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- журнал модерации

interface ModerationLogRow {
  id: string;
  action: string;
  from: string | null;
  to: string | null;
  reasonCode: string | null;
  reasonText: string | null;
  note: string | null;
  admin: string | null;
  listing: { id: string; title: string; status: string };
  at: string;
}

export function ModerationLogPage() {
  const { can } = useAdmin();
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['moderation-logs', action, page],
    queryFn: () => api<Paged<ModerationLogRow>>(`/admin/moderation-logs${qs({ action, page, pageSize: 50 })}`),
  });
  return (
    <div>
      <h1>Журнал модерации</h1>
      <div className="toolbar">
        <select value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} aria-label="Действие">
          <option value="">Все действия</option>
          {Object.entries(ACTION_RU).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {isLoading ? <Loading /> : error ? <ErrorBox error={error} onRetry={() => void refetch()} /> : !data?.items.length ? <Empty /> : (
        <>
          <table>
            <thead><tr><th>Время</th><th>Объявление</th><th>Действие</th><th>Причина / примечание</th><th>Кто</th></tr></thead>
            <tbody>
              {data.items.map((m) => (
                <tr key={m.id}>
                  <td>{fmtDate(m.at)}</td>
                  <td>#{m.listing.id} {m.listing.title}<div className="muted">сейчас: {STATUS_RU[m.listing.status] ?? m.listing.status}</div></td>
                  <td>{ACTION_RU[m.action] ?? m.action}{m.from ? <div className="muted">{STATUS_RU[m.from] ?? m.from} → {STATUS_RU[m.to ?? ''] ?? m.to}</div> : null}</td>
                  <td>
                    {m.reasonCode ? `${REJECT_REASONS[m.reasonCode] ?? m.reasonCode}${m.reasonText ? `: ${m.reasonText}` : ''}` : ''}
                    {m.note ? <div className="muted">{m.note}</div> : null}
                  </td>
                  <td>{m.admin ?? <span className="muted">система</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pageSize={50} total={data.total} onPage={setPage} />
        </>
      )}
      {can('audit.view') ? <p className="muted">Все действия администраторов (не только модерация) — в разделе «Журнал».</p> : null}
    </div>
  );
}
